/**
 * SeatCanvas.tsx
 * 공유 좌석 캔버스 컴포넌트
 *
 * 통합레이아웃과 출석부에서 동일하게 사용하는 좌석 렌더링 모델
 * - mode="edit": 드래그 가능, 그리드 라인 표시, 충돌 감지
 * - mode="attendance": 클릭만 가능, 출석 상태 색상, 학생 이름 표시
 * - mode="view": 읽기 전용
 */

import React, { useRef, useCallback, useState } from 'react';

// ============================================================================
// 상수
// ============================================================================
export const CELL_SIZE = 80; // 통합레이아웃 기준 (layoutRenderers.tsx)
export const SNAP_SIZE = CELL_SIZE * 0.2; // 스냅 크기 (16px)

// 구역 색상 팔레트
// 2026-10-12 연한 색(파스텔) 추가 — 우장산점 요청. 진한 빨강 구역 띠가 미출석(빨강)과 헷갈렸다.
//   🔴 연한 색에 빨강·분홍·보라 계열은 넣지 않는다 — 미출석 바탕(연빨강)·출석 면제(연보라)와 겹친다.
//   `light: true` 인 색은 선택 창에서 '연한 색' 줄에 따로 보인다.
export const ZONE_COLORS: { name: string; value: string; light?: boolean }[] = [
  { name: '연하늘', value: '#93C5FD', light: true },
  { name: '연청록', value: '#67E8F9', light: true },
  { name: '연민트', value: '#6EE7B7', light: true },
  { name: '연연두', value: '#BEF264', light: true },
  { name: '연노랑', value: '#FDE68A', light: true },
  { name: '연살구', value: '#FED7AA', light: true },
  { name: '연회색', value: '#CBD5E1', light: true },
  { name: '파란색', value: '#3B82F6' },
  { name: '초록색', value: '#10B981' },
  { name: '빨간색', value: '#EF4444' },
  { name: '보라색', value: '#8B5CF6' },
  { name: '노란색', value: '#F59E0B' },
  { name: '분홍색', value: '#EC4899' },
  { name: '청록색', value: '#06B6D4' },
  { name: '주황색', value: '#F97316' },
  { name: '회색', value: '#6B7280' },
  { name: '갈색', value: '#92400E' },
];

/** 밝은 색인지 — 연한 구역 색 위에 흰 글자를 올리면 안 보이므로 글자색을 고를 때 쓴다. */
export const isLightColor = (hex?: string | null): boolean => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
};

/**
 * 좌석 바탕에 깔 구역 음영. 진한 색은 10%로 옅게, 연한 색은 이미 옅으므로 45%로 깐다
 * (연한 색을 10%로 깔면 흰색과 구분이 안 된다). '#RRGGBB' 가 아니면 종전처럼 10%.
 */
export const zoneTint = (hex: string): string => `${hex}${isLightColor(hex) ? '73' : '1A'}`;

// 출석 상태별 하단 바 색상
export const ATTENDANCE_BAR_COLORS: Record<string, string> = {
  present: '#22c55e',     // 출석 - 초록
  late: '#f97316',        // (레거시) 주황
  absent: '#ef4444',      // 미출석 - 빨강
  out: '#3b82f6',         // 외출 - 파랑
  arriving: '#f97316',    // 곧 입실 - 주황
  exited: '#9ca3af',      // 퇴실 - 회색
  not_scheduled: '#9ca3af', // 스케줄없음 - 회색
  exempt: '#8b5cf6',       // 출석 면제 - 보라
  membership_expired: '#111827',   // 만료 회원 - 검정
  membership_suspended: '#111827', // 정지(퇴원) 회원 - 검정
};

// ============================================================================
// 타입 정의
// ============================================================================
export type SeatCanvasMode = 'edit' | 'attendance' | 'view';

export interface CanvasSeatData {
  // 필수 필드
  seat_number: number;
  position_x: number;
  position_y: number;

  // 선택 필드 - 공통
  seat_id?: string;
  id?: string;
  seat_code?: string;
  zone_id?: string;
  zone_color?: string;
  zone_name?: string;
  is_occupied?: boolean;
  is_available?: boolean;
  is_active?: boolean;

  // 출석 모드 전용
  student_id?: string;
  student_name?: string;
  schedule_status?: 'present' | 'absent' | 'out' | 'exited' | 'arriving' | 'not_scheduled' | 'exempt' | 'membership_expired' | 'membership_suspended';
  is_late?: boolean;
  is_arriving_soon?: boolean;
  outing_type?: string;
  return_overdue_minutes?: number;
  expected_check_in?: string;
  expected_check_out?: string;
  academy_leave_time?: string;
  expected_return_time?: string;

  // 연락 메모 (오늘 날짜)
  has_today_memo?: boolean;
  today_memo_content?: string;

  // 스케줄을 아예 한 번도 안 낸 학생 (오늘만 쉬는 날인 것과 구분)
  schedule_never_submitted?: boolean;
}

export interface CanvasZoneData {
  id: string;
  name?: string;
  color?: string;
}

export interface DraggingSeatState {
  seatNumber: number;
  x: number;
  y: number;
  isColliding: boolean;
  errorMessage: string;
}

export interface SeatCanvasProps {
  // 필수
  seats: CanvasSeatData[];
  canvasWidth: number;  // 픽셀 단위
  canvasHeight: number; // 픽셀 단위
  mode: SeatCanvasMode;

  // 선택 - 공통
  zones?: CanvasZoneData[];
  selectedSeatId?: string | number;
  onSeatClick?: (seatId: string, seat: CanvasSeatData) => void;
  scale?: number;

  // 선택 - 편집 모드
  onSeatDragEnd?: (seatNumber: number, newX: number, newY: number) => void;
  onSeatDragStart?: (seatNumber: number) => void;
  showGridLines?: boolean;

  // 선택 - 출석 모드
  showLegend?: boolean;
}

// ============================================================================
// 개별 좌석 컴포넌트 (export하여 IntegratedLayoutManagement에서도 사용)
// ============================================================================
export interface SeatItemProps {
  seat: CanvasSeatData;
  mode: SeatCanvasMode;
  isHighlighted: boolean;
  zoneColor: string;
  onClick: () => void;
  // 2026-10-04 구역 표시 — 구역이 있는 좌석만 값이 온다. 위쪽 띠 + (흰 카드일 때) 옅은 음영.
  zoneAccent?: string | null;
  zoneLabel?: string | null;

  // 편집 모드용
  isEditMode?: boolean;
  isDimmed?: boolean;  // 흐리게 표시 (구역 필터링)
  isBeingDragged?: boolean;
  displayX?: number;
  displayY?: number;
  isColliding?: boolean;
  errorMessage?: string;
  onMouseDown?: (e: React.MouseEvent) => void;
}

export const SeatItem: React.FC<SeatItemProps> = ({
  seat,
  mode,
  isHighlighted,
  zoneColor,
  onClick,
  zoneAccent = null,
  zoneLabel = null,
  isEditMode = false,
  isDimmed = false,
  isBeingDragged = false,
  displayX,
  displayY,
  isColliding = false,
  errorMessage = '',
  onMouseDown,
}) => {
  // 실제 표시 위치
  const x = displayX ?? seat.position_x;
  const y = displayY ?? seat.position_y;

  // ========== 배경/테두리 클래스 ==========
  const getContainerClass = () => {
    // 충돌 상태 (편집 모드)
    if (isColliding) {
      return 'bg-red-100 border-red-500';
    }
    // 출석 모드: 미출석만 빨간 배경, 나머지는 흰색
    if (mode === 'attendance') {
      const status = seat.is_arriving_soon ? 'arriving' : seat.schedule_status;
      if (status === 'absent') return 'bg-red-100 border-red-400';
      if (status === 'exempt') return 'bg-purple-100 border-purple-400';
      return 'bg-white border-gray-300';
    }
    // 사용중
    if (seat.is_occupied) {
      return 'bg-red-100 border-red-400';
    }
    // 선택됨 (하이라이트)
    if (isHighlighted) {
      return 'bg-blue-100 border-blue-500';
    }
    // 기본
    return 'bg-white border-gray-300';
  };

  // ========== 그림자 스타일 ==========
  const getBoxShadow = () => {
    // 충돌 상태
    if (isColliding) {
      return '0 0 15px rgba(239, 68, 68, 0.5)';
    }
    // 출석 모드: 미복귀 경고
    if (mode === 'attendance' && seat.schedule_status === 'out' &&
        seat.return_overdue_minutes && seat.return_overdue_minutes > 0) {
      const overdueColor = seat.return_overdue_minutes > 5 ? '#ef4444' : '#f97316';
      return `0 2px 4px rgba(0,0,0,0.1), inset 0 0 0 3px ${overdueColor}`;
    }
    // 선택됨
    if (isHighlighted) {
      return '0 0 12px rgba(59, 130, 246, 0.6)';
    }
    // 기본
    return '0 2px 4px rgba(0,0,0,0.1)';
  };

  // ========== 좌석 번호 색상 ==========
  const getNumberColor = () => {
    if (isColliding) return 'text-red-600';
    if (mode === 'attendance') {
      const status = seat.is_arriving_soon ? 'arriving' : seat.schedule_status;
      if (status === 'absent') return 'text-red-600';
      return 'text-gray-700';
    }
    if (seat.is_occupied) return 'text-red-600';
    return 'text-gray-700';
  };

  // ========== 상태 텍스트 색상 ==========
  const getStatusColor = () => {
    if (isColliding) return 'text-red-500';
    if (mode === 'attendance') {
      const status = seat.is_arriving_soon ? 'arriving' : seat.schedule_status;
      if (status === 'absent') return 'text-red-500';
      return 'text-gray-600';
    }
    if (seat.is_occupied) return 'text-red-500';
    return 'text-gray-400';
  };

  // ========== 상태 텍스트 내용 ==========
  const getStatusText = () => {
    if (isColliding) return '❌ 불가';

    if (mode === 'attendance') {
      // 출석 모드: 학생 이름 또는 빈좌석
      return seat.student_name || '빈좌석';
    }
    // 편집/뷰 모드: 사용중 또는 빈좌석
    return seat.is_occupied ? '사용중' : '빈좌석';
  };

  // 외출 라벨 (이름 아래 별도 표시: 이모티콘 + 텍스트)
  const OUTING_LABEL: Record<string, string> = {
    meal: '🍚식사',
    hospital: '🏥병원',
    academy: '📚학원',
    rest: '☕휴식',
    toilet: '🚻화장실',
    other: '📝기타',
  };

  const getOutingLabel = () => {
    if (mode === 'attendance' && seat.schedule_status === 'out' && seat.outing_type) {
      return OUTING_LABEL[seat.outing_type] || '외출';
    }
    return null;
  };

  // ========== 하단 바 색상 ==========
  const getBarColor = () => {
    if (isColliding) return '#EF4444';

    if (mode === 'attendance') {
      // 출석 모드: 출석 상태별 색상
      if (seat.is_arriving_soon) return ATTENDANCE_BAR_COLORS.arriving;
      const status = seat.schedule_status || 'not_scheduled';
      return ATTENDANCE_BAR_COLORS[status] || ATTENDANCE_BAR_COLORS.not_scheduled;
    }
    // 편집/뷰 모드: 구역 색상
    return zoneColor;
  };

  // 표시할 좌석 번호
  const displayNumber = seat.seat_code || seat.seat_number;
  // 출석부에서 구역이 있는 좌석은 왼쪽 위 모서리를 구역 색 삼각으로 (2026-10-17 대표 결정)
  const zoneBadge = mode === 'attendance' && !!zoneAccent;

  return (
    <div
      className={`absolute select-none ${
        isEditMode ? 'cursor-move' : 'cursor-pointer'
      }`}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${CELL_SIZE}px`,
        height: `${CELL_SIZE}px`,
        transform: isHighlighted ? 'scale(1.05)' : 'scale(1)',
        zIndex: isBeingDragged ? 100 : (isHighlighted ? 10 : 1),
        transition: isBeingDragged ? 'none' : 'all 0.15s ease',
        opacity: isDimmed ? 0.3 : 1,
        // 스케줄 미제출 — 색은 이미 출결 상태가 다 쓰고 있어 '모양'(점선 테두리)으로 구분한다.
        // outline 을 쓰는 이유: border 를 건드리면 아래쪽 6px 상태바까지 점선이 된다.
        ...(mode === 'attendance' && seat.schedule_never_submitted
          ? { outline: '2px dashed #d97706', outlineOffset: '2px' }
          : {}),
      }}
      onMouseDown={isEditMode ? onMouseDown : undefined}
      onClick={!isBeingDragged ? onClick : undefined}
    >
      {/* 좌석 네모칸 */}
      <div
        className={`relative w-full h-full rounded-lg border-2 flex flex-col items-center justify-center shadow-md ${getContainerClass()}`}
        title={zoneAccent && zoneLabel ? `구역: ${zoneLabel}` : undefined}
        style={{
          boxShadow: getBoxShadow(),
          borderBottomWidth: '6px',
          borderBottomColor: getBarColor(),
          // 구역 표시 (2026-10-04 대표 요청) — 위 띠 = 구역, 아래 띠 = 출결.
          //   바탕색·안쪽 테두리·점선 바깥 테두리는 이미 출결 표시가 쓰고 있어 건드리지 않는다.
          //   음영은 흰 카드일 때만 — 미출석(빨강)·출석면제(보라) 바탕을 덮지 않게.
          // 2026-10-17 대표 결정 — 출석부(attendance)에서는 음영을 깔지 않고 위쪽 띠만 남긴다.
          //   더그릿 송파점 문의: 구역 음영 때문에 출석부가 보기 불편함. 배치도 편집 화면(edit)은
          //   좌석을 구역에 넣을 때 한눈에 봐야 하므로 음영을 그대로 둔다.
          //   출석부는 띠 대신 왼쪽 위 삼각 모서리를 쓴다(아래 zoneBadge).
          ...(zoneAccent && !zoneBadge ? {
            borderTopWidth: '5px',
            borderTopColor: zoneAccent,
            ...(getContainerClass().includes('bg-white') ? { backgroundColor: zoneTint(zoneAccent) } : {}),
          } : {}),
        }}
      >
        {/* 출석부 구역 표시 — 왼쪽 위 모서리를 구역 색 삼각으로 접은 모양 (2026-10-17 대표 결정, 시안 8개 중 7번)
            띠·네모는 아래쪽 출결 띠와 모양이 같아 '상태'로 읽혔다. 모서리는 번호·이름·출결 표시를 건드리지 않는다.
            연한 색은 흰 바탕에 묻히므로 옅은 윤곽을 준다. 둥근 모서리 밖으로 삐져나오지 않게 감싸서 자른다. */}
        {zoneBadge && (
          <span
            className="absolute pointer-events-none"
            title={zoneLabel ? `구역: ${zoneLabel}` : undefined}
            // 외출 미복귀면 안쪽 빨강·주황 테두리(3px)를 가리지 않게 그 안쪽으로 들인다
            style={(() => { const inset = seat.schedule_status === 'out' && (seat.return_overdue_minutes || 0) > 0 ? 3 : 0;
              return { top: inset, left: inset, width: 22, height: 22, overflow: 'hidden', borderTopLeftRadius: inset ? 3 : 6 }; })()}
          >
            <span
              style={{
                position: 'absolute', top: 0, left: 0, width: 0, height: 0,
                borderTop: `22px solid ${zoneAccent}`, borderRight: '22px solid transparent',
                ...(isLightColor(zoneAccent) ? { filter: 'drop-shadow(0 0 0.6px rgba(0,0,0,0.45))' } : {}),
              }}
            />
          </span>
        )}
        {/* 좌석 번호 */}
        <span
          className={`font-bold ${getNumberColor()}`}
          style={{ fontSize: CELL_SIZE > 60 ? '18px' : '14px' }}
        >
          {displayNumber}
        </span>

        {/* 상태 표시 (이름) — '빈좌석' placeholder는 여리게(연한 회색+반투명) 표시 */}
        <span
          className={`text-xs mt-0.5 ${
            getStatusText() === '빈좌석'
              ? 'text-gray-300 font-light opacity-50'
              : getStatusColor()
          }`}
        >
          {getStatusText()}
        </span>

        {/* 만료/정지(퇴원) 회원 배지 — 검정 */}
        {mode === 'attendance' &&
          (seat.schedule_status === 'membership_expired' || seat.schedule_status === 'membership_suspended') && (
          <span className="text-xs font-semibold text-white bg-gray-900 px-1 rounded mt-0.5">
            {seat.schedule_status === 'membership_expired' ? '만료' : '정지'}
          </span>
        )}

        {/* 외출 타입 라벨 — academy 는 학원 스케줄 있을 때만 복귀시간으로 대체, 없으면 기본 라벨 표시 */}
        {getOutingLabel() && (
          seat.outing_type !== 'academy' || !seat.expected_return_time
        ) && (
          <span className="text-xs font-semibold text-blue-700 bg-blue-200 px-1 rounded mt-0.5">
            {getOutingLabel()}
          </span>
        )}

        {/* 학원 외출: 복귀 예정 시간 (학원 스케줄 등록돼 있을 때만) */}
        {mode === 'attendance' && seat.schedule_status === 'out' && seat.outing_type === 'academy' && seat.expected_return_time && (
          <span className="text-xs font-semibold text-blue-700 bg-blue-200 px-1 rounded mt-0.5">
            🔙{seat.expected_return_time}
          </span>
        )}

        {/* 입실 학생: 학원 출발 시간 (보라색 뱃지) */}
        {mode === 'attendance' && seat.schedule_status === 'present' && seat.academy_leave_time && (
          <span className="text-xs font-semibold text-purple-700 bg-purple-200 px-1 rounded mt-0.5">
            📚{seat.academy_leave_time}
          </span>
        )}

        {/* 입실 학생: 퇴실 예정 시간 (학원 일정 없을 때만) */}
        {mode === 'attendance' && seat.schedule_status === 'present' && !seat.academy_leave_time && seat.expected_check_out && (
          <span className="text-xs text-gray-400 mt-0.5">
            ~{seat.expected_check_out}
          </span>
        )}

        {/* 미입실 학생: 입실 예정 시간 (스케줄 있는 모든 미입실 학생) */}
        {mode === 'attendance' && seat.schedule_status !== 'present' && seat.schedule_status !== 'out' && seat.expected_check_in && (
          <span className={`text-xs mt-0.5 ${
            seat.schedule_status === 'absent' ? 'text-red-400' :
            seat.schedule_status === 'arriving' ? 'text-orange-400' :
            'text-gray-400'
          }`}>
            {seat.expected_check_in}~{seat.expected_check_out || ''}
          </span>
        )}
      </div>

      {/* 연락 메모 점 표시 (오늘 날짜 메모 있을 때) */}
      {mode === 'attendance' && seat.has_today_memo && (
        <div
          className="absolute w-5 h-5 bg-orange-500 rounded-full border-2 border-white shadow-md z-20 flex items-center justify-center"
          title={seat.today_memo_content || '연락 메모'}
          style={{ top: '2px', right: '6px' }}
        >
          <span style={{ fontSize: '10px', lineHeight: 1 }}>📞</span>
        </div>
      )}

      {/* 에러 메시지 (충돌 시) */}
      {isColliding && errorMessage && (
        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs bg-red-600 text-white px-2 py-1 rounded whitespace-nowrap shadow-lg z-50">
          ❌ {errorMessage}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 출석 범례 컴포넌트
// ============================================================================
const AttendanceLegend: React.FC<{ zoneItems?: { name: string; color: string }[] }> = ({ zoneItems = [] }) => {
  // 🔴 좁은 화면(휴대폰)에서는 **접힌 채로 시작**한다 (2026-09-05).
  //    범례가 좌석 위에 떠 있어서, 펼쳐진 상태로 열리면 자리 절반을 덮어
  //    누가 앉아 있는지 보이지 않았다. 제목을 누르면 펼칠 수 있다.
  const [collapsed, setCollapsed] = React.useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  return (
    <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
      <div
        className="flex items-center justify-between px-4 py-2 cursor-pointer select-none"
        onClick={() => setCollapsed(!collapsed)}
      >
        <h4 className="text-sm font-semibold text-gray-700">출석 상태</h4>
        <span className="text-gray-400 text-xs ml-2">{collapsed ? '펼치기' : '접기'}</span>
      </div>
      {!collapsed && (
        <div className="px-4 pb-3 space-y-1.5">
          {[
            { color: ATTENDANCE_BAR_COLORS.present, label: '출석' },
            { color: ATTENDANCE_BAR_COLORS.arriving, label: '곧 입실' },
            { color: ATTENDANCE_BAR_COLORS.out, label: '외출' },
            { color: ATTENDANCE_BAR_COLORS.absent, label: '미출석' },
            { color: ATTENDANCE_BAR_COLORS.exempt, label: '출석 면제' },
            { color: ATTENDANCE_BAR_COLORS.not_scheduled, label: '스케줄 없음' },
            { color: ATTENDANCE_BAR_COLORS.membership_expired, label: '만료/정지 회원' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
              <span className="text-xs text-gray-600">{label}</span>
            </div>
          ))}
          <div className="border-t pt-2 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: ATTENDANCE_BAR_COLORS.out, borderColor: '#f97316' }} />
              <span className="text-xs text-gray-600">미복귀 (5분 이하)</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: ATTENDANCE_BAR_COLORS.out, borderColor: '#ef4444' }} />
              <span className="text-xs text-gray-600">미복귀 (5분 초과)</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center">
                <span style={{ fontSize: '8px' }}>📞</span>
              </div>
              <span className="text-xs text-gray-600">연락 메모</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div
                className="w-4 h-4 rounded bg-white"
                style={{ outline: '2px dashed #d97706', outlineOffset: '-1px' }}
              />
              <span className="text-xs text-gray-600">스케줄 미제출</span>
            </div>
          </div>
          {/* 구역 (2026-10-04) — 좌석 위쪽 색 띠. 구역이 있는 방에서만 보인다 */}
          {zoneItems.length > 0 && (
            <div className="border-t pt-2 mt-2">
              <div className="text-xs font-semibold text-gray-500 mb-1">구역 (왼쪽 위 모서리)</div>
              {zoneItems.map((z) => (
                <div key={z.name + z.color} className="flex items-center gap-2 mt-1">
                  <span className="relative inline-block w-4 h-4 rounded-sm bg-white border border-gray-300 overflow-hidden"><span style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, borderTop: `10px solid ${z.color}`, borderRight: '10px solid transparent' }} /></span>
                  <span className="text-xs text-gray-600">{z.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 메인 SeatCanvas 컴포넌트
// ============================================================================
export const SeatCanvas: React.FC<SeatCanvasProps> = ({
  seats,
  canvasWidth,
  canvasHeight,
  mode,
  zones = [],
  selectedSeatId,
  onSeatClick,
  scale = 1,
  onSeatDragEnd,
  onSeatDragStart,
  showGridLines = true,
  showLegend = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isEditMode = mode === 'edit';

  // 드래그 상태 (편집 모드)
  const [draggingSeat, setDraggingSeat] = useState<DraggingSeatState | null>(null);
  const dragStartRef = useRef<{
    seatNumber: number;
    startX: number;
    startY: number;
    originalX: number;
    originalY: number;
    offsetX: number;
    offsetY: number;
    hasMoved: boolean;
  } | null>(null);

  // 구역 색상 맵
  const zoneColorMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    zones.forEach(zone => {
      if (zone.color) {
        map[zone.id] = zone.color;
      }
    });
    return map;
  }, [zones]);

  // 구역 이름 맵 / 구역 표시 색 — 구역이 실제로 있는 좌석만 (없으면 null → 지금처럼 보인다)
  const zoneNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    zones.forEach(zone => { if (zone.name) map[zone.id] = zone.name; });
    return map;
  }, [zones]);
  const zoneAccentOf = (seat: CanvasSeatData): string | null => {
    if (seat.zone_color) return seat.zone_color;
    if (seat.zone_id && zoneColorMap[seat.zone_id]) return zoneColorMap[seat.zone_id];
    return null;
  };

  // 범례용 — 이 화면 좌석에 실제로 있는 구역만
  const legendZones = React.useMemo(() => {
    const seen = new Map<string, { name: string; color: string }>();
    seats.forEach((st) => {
      const color = st.zone_color || (st.zone_id ? zoneColorMap[st.zone_id] : undefined);
      const name = st.zone_name || (st.zone_id ? zoneNameMap[st.zone_id] : undefined);
      if (color && name && !seen.has(name)) seen.set(name, { name, color });
    });
    return Array.from(seen.values());
  }, [seats, zoneColorMap, zoneNameMap]);

  // 좌석의 구역 색상 가져오기
  const getZoneColor = (seat: CanvasSeatData): string => {
    if (seat.zone_color) return seat.zone_color;
    if (seat.zone_id && zoneColorMap[seat.zone_id]) return zoneColorMap[seat.zone_id];
    return '#3B82F6'; // 기본 파란색
  };

  // ========== 충돌 감지 (편집 모드) ==========
  const checkCollision = useCallback((movingSeatNumber: number, newX: number, newY: number): CanvasSeatData | null => {
    const margin = 4;
    for (const seat of seats) {
      if (seat.seat_number === movingSeatNumber) continue;

      const dx = Math.abs(newX - seat.position_x);
      const dy = Math.abs(newY - seat.position_y);

      if (dx < CELL_SIZE - margin && dy < CELL_SIZE - margin) {
        return seat;
      }
    }
    return null;
  }, [seats]);

  // ========== 드래그 핸들러 (편집 모드) ==========
  const handleMouseDown = useCallback((seat: CanvasSeatData, e: React.MouseEvent) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();

    const seatElement = e.currentTarget as HTMLElement;
    const seatRect = seatElement.getBoundingClientRect();
    const offsetX = e.clientX - seatRect.left;
    const offsetY = e.clientY - seatRect.top;

    dragStartRef.current = {
      seatNumber: seat.seat_number,
      startX: e.clientX,
      startY: e.clientY,
      originalX: seat.position_x,
      originalY: seat.position_y,
      offsetX,
      offsetY,
      hasMoved: false,
    };

    setDraggingSeat({
      seatNumber: seat.seat_number,
      x: seat.position_x,
      y: seat.position_y,
      isColliding: false,
      errorMessage: '',
    });

    onSeatDragStart?.(seat.seat_number);
  }, [isEditMode, onSeatDragStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragStartRef.current || !containerRef.current || !isEditMode) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    // 스케일 적용된 좌표 계산
    const rawX = (e.clientX - containerRect.left) / scale - dragStartRef.current.offsetX / scale;
    const rawY = (e.clientY - containerRect.top) / scale - dragStartRef.current.offsetY / scale;

    // 스냅 적용
    const snappedX = Math.round(rawX / SNAP_SIZE) * SNAP_SIZE;
    const snappedY = Math.round(rawY / SNAP_SIZE) * SNAP_SIZE;

    // 경계 체크
    const maxX = canvasWidth - CELL_SIZE;
    const maxY = canvasHeight - CELL_SIZE;
    const newX = Math.max(0, Math.min(snappedX, maxX));
    const newY = Math.max(0, Math.min(snappedY, maxY));

    // 충돌 체크
    const collidingSeat = checkCollision(dragStartRef.current.seatNumber, newX, newY);

    dragStartRef.current.hasMoved = true;

    setDraggingSeat({
      seatNumber: dragStartRef.current.seatNumber,
      x: newX,
      y: newY,
      isColliding: !!collidingSeat,
      errorMessage: collidingSeat ? `${collidingSeat.seat_number}번 좌석과 겹침` : '',
    });
  }, [isEditMode, scale, canvasWidth, canvasHeight, checkCollision]);

  const handleMouseUp = useCallback(() => {
    if (!dragStartRef.current || !draggingSeat || !isEditMode) return;

    if (dragStartRef.current.hasMoved && !draggingSeat.isColliding) {
      onSeatDragEnd?.(draggingSeat.seatNumber, draggingSeat.x, draggingSeat.y);
    }

    setDraggingSeat(null);
    dragStartRef.current = null;
  }, [draggingSeat, isEditMode, onSeatDragEnd]);

  const handleMouseLeave = useCallback(() => {
    if (draggingSeat) {
      setDraggingSeat(null);
      dragStartRef.current = null;
    }
  }, [draggingSeat]);

  // 그리드 눈금선 계산
  const gridLinesX = Math.floor(canvasWidth / SNAP_SIZE) + 1;
  const gridLinesY = Math.floor(canvasHeight / SNAP_SIZE) + 1;

  return (
    <div className="relative overflow-auto">
      {/* 캔버스 영역 */}
      <div
        ref={containerRef}
        className="relative border border-gray-300 rounded-lg"
        style={{
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          backgroundColor: draggingSeat && isEditMode ? 'rgba(59, 130, 246, 0.1)' : '#fafafa',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
        onMouseMove={isEditMode ? handleMouseMove : undefined}
        onMouseUp={isEditMode ? handleMouseUp : undefined}
        onMouseLeave={isEditMode ? handleMouseLeave : undefined}
      >
        {/* 그리드 눈금선 (편집 모드에서만) */}
        {isEditMode && showGridLines && (
          <svg
            className="absolute inset-0 pointer-events-none"
            width={canvasWidth}
            height={canvasHeight}
            style={{ zIndex: 0 }}
          >
            {/* 세로 눈금선 */}
            {Array.from({ length: gridLinesX }).map((_, i) => {
              const x = i * SNAP_SIZE;
              const isMajor = x % CELL_SIZE === 0;
              return (
                <line
                  key={`v-${i}`}
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={canvasHeight}
                  stroke={isMajor ? '#9CA3AF' : '#E5E7EB'}
                  strokeWidth={isMajor ? 1.5 : 0.5}
                />
              );
            })}
            {/* 가로 눈금선 */}
            {Array.from({ length: gridLinesY }).map((_, i) => {
              const y = i * SNAP_SIZE;
              const isMajor = y % CELL_SIZE === 0;
              return (
                <line
                  key={`h-${i}`}
                  x1={0}
                  y1={y}
                  x2={canvasWidth}
                  y2={y}
                  stroke={isMajor ? '#9CA3AF' : '#E5E7EB'}
                  strokeWidth={isMajor ? 1.5 : 0.5}
                />
              );
            })}
            {/* 좌표 레이블 (100px 단위) */}
            {Array.from({ length: Math.floor(canvasWidth / 100) + 1 }).map((_, i) => (
              <text
                key={`lx-${i}`}
                x={i * 100 + 2}
                y={12}
                fontSize={10}
                fill="#6B7280"
              >
                {i * 100}
              </text>
            ))}
            {Array.from({ length: Math.floor(canvasHeight / 100) + 1 }).map((_, i) => (
              i > 0 && (
                <text
                  key={`ly-${i}`}
                  x={2}
                  y={i * 100 + 12}
                  fontSize={10}
                  fill="#6B7280"
                >
                  {i * 100}
                </text>
              )
            ))}
          </svg>
        )}

        {/* 좌석들 */}
        {seats.map((seat) => {
          const seatId = seat.seat_id || seat.id || String(seat.seat_number);
          const isHighlighted = selectedSeatId === seatId || selectedSeatId === seat.seat_number;
          const isBeingDragged = draggingSeat?.seatNumber === seat.seat_number;

          return (
            <SeatItem
              key={seatId}
              seat={seat}
              mode={mode}
              isHighlighted={isHighlighted}
              zoneColor={getZoneColor(seat)}
              zoneAccent={zoneAccentOf(seat)}
              zoneLabel={seat.zone_name || (seat.zone_id ? zoneNameMap[seat.zone_id] : null) || null}
              onClick={() => onSeatClick?.(seatId, seat)}
              isEditMode={isEditMode}
              isBeingDragged={isBeingDragged}
              displayX={isBeingDragged ? draggingSeat?.x : undefined}
              displayY={isBeingDragged ? draggingSeat?.y : undefined}
              isColliding={isBeingDragged ? draggingSeat?.isColliding : false}
              errorMessage={isBeingDragged ? draggingSeat?.errorMessage : ''}
              onMouseDown={(e) => handleMouseDown(seat, e)}
            />
          );
        })}
      </div>

      {/* 범례 (출석 모드에서만) */}
      {mode === 'attendance' && showLegend && <AttendanceLegend zoneItems={legendZones} />}
    </div>
  );
};

export default SeatCanvas;
